import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { OrganizationInvitation } from '@robocorp/robocloud-internal-cds-sdk';
import { useIsSSO } from '~/hooks/useIsSSO';
import { organizationService as userOrganizationService } from '../../services/userAPI';
import { organizationService } from '../../services/organizationAPI';
import { getOrganizationsQueryKey } from './useOrganizationsQuery';
import { getWorkspacesQueryKey } from '../workspace/useWorkspacesQuery';
import { formatErrorPayload, validateApiResponse } from '../utils';

export enum OrganizationInvitesErrors {
  GET_ORGANIZATION_INVITES_ERROR = 'GET_ORGANIZATION_INVITES_ERROR',
  INVITE_USER_TO_ORGANIZATION_ERROR = 'INVITE_USER_TO_ORGANIZATION_ERROR',
  DELETE_ORGANIZATION_INVITE_ERROR = 'DELETE_ORGANIZATION_INVITE_ERROR',
  SET_ORGANIZATION_INVITE_ROLE_ERROR = 'SET_ORGANIZATION_INVITE_ROLE_ERROR',
  GET_ORGANIZATION_INVITE_ERROR = 'GET_ORGANIZATION_INVITE_ERROR',
  GET_USER_ORGANIZATION_INVITES_ERROR = 'GET_USER_ORGANIZATION_INVITES_ERROR',
  ACCEPT_ORGANIZATION_INVITE_ERROR = 'ACCEPT_ORGANIZATION_INVITE_ERROR',
}

export const getOrganizationInvitesQueryKey = (organizationId: string) => [
  'organizationInvites',
  organizationId,
];
export const getUserOrganizationInvitesQueryKey = () => ['userOrganizationInvites'];
export const getOrganizationInviteQueryKey = (token: string | undefined) => [
  'organizationInvite',
  token,
];

export function useOrganizationInvitesQuery(organizationId: string) {
  const dispatch = useDispatch();
  const { realm } = useIsSSO();
  return useQuery(
    getOrganizationInvitesQueryKey(organizationId),
    async () => {
      const response = validateApiResponse(
        await organizationService.getOrganizationOutgoingInvites({
          organizationId,
          realmId: realm,
        }),
      );
      return response.data;
    },
    {
      onError: (error: Error) =>
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationInvitesErrors.GET_ORGANIZATION_INVITES_ERROR,
            organizationId,
          }),
        ),
    },
  );
}

export function useOrganizationInviteMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { realm } = useIsSSO();

  return useMutation(
    async ({ organizationId, email }: { organizationId: string; email: string }) => {
      const response = validateApiResponse(
        await organizationService.inviteUser(organizationId, { email, realmId: realm }),
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (!data) return;
        queryClient.invalidateQueries(getOrganizationInvitesQueryKey(data.organization_id));
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationInvitesErrors.INVITE_USER_TO_ORGANIZATION_ERROR,
          }),
        );
      },
    },
  );
}

export function useOrganizationDeleteInviteMutation(organizationId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ inviteId }: { inviteId: string }) => {
      const response = validateApiResponse(
        await organizationService.deleteOrganizationInvite(organizationId, inviteId),
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (!data) return;
        queryClient.invalidateQueries(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          getOrganizationInvitesQueryKey((data as any)?.organization_id),
        );
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationInvitesErrors.DELETE_ORGANIZATION_INVITE_ERROR,
          }),
        );
      },
    },
  );
}

export function useOrganizationSetInviteRoleMutation(organizationId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<
    OrganizationInvitation | undefined,
    Error,
    {
      inviteId: string;
      roleId: string;
    },
    { previousInvites: OrganizationInvitation[] | null }
  >(
    async ({ inviteId, roleId }) => {
      const response = validateApiResponse(
        await organizationService.setOrganizationInviteRole(organizationId, inviteId, roleId),
      );
      return response.data;
    },
    {
      onMutate: async ({ inviteId, roleId }) => {
        await queryClient.cancelQueries(getOrganizationInvitesQueryKey(organizationId));

        const previousInvites =
          queryClient.getQueryData<OrganizationInvitation[]>(
            getOrganizationInvitesQueryKey(organizationId),
          ) ?? [];

        const idx = previousInvites.findIndex(({ id }) => id === inviteId);
        if (idx === -1) return { previousInvites: null };

        const next = [...previousInvites];
        const updated = { ...next[idx], org_role_id: roleId };
        next.splice(idx, 1, updated);

        queryClient.setQueryData(getOrganizationInvitesQueryKey(organizationId), next);

        return { previousInvites };
      },
      onSuccess: () => {
        queryClient.invalidateQueries(getOrganizationInvitesQueryKey(organizationId));
      },
      onError: (error: Error, args, context) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationInvitesErrors.SET_ORGANIZATION_INVITE_ROLE_ERROR,
          }),
        );

        queryClient.setQueryData(
          getOrganizationInvitesQueryKey(organizationId),
          context?.previousInvites,
        );
      },
    },
  );
}

export function useUserOrganizationInvites() {
  const dispatch = useDispatch();
  const { realm } = useIsSSO();

  return useQuery(
    getUserOrganizationInvitesQueryKey(),
    async () => {
      const response = validateApiResponse(
        await userOrganizationService.getUserOrganizationInvites({ realmId: realm }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch({
          ...formatErrorPayload(error, {
            type: OrganizationInvitesErrors.GET_USER_ORGANIZATION_INVITES_ERROR,
          }),
          showSnackbar: false,
        });
      },
    },
  );
}

export function useOrganizationInviteQuery(token: string) {
  const dispatch = useDispatch();
  return useQuery(
    getOrganizationInviteQueryKey(token),
    async () => {
      const response = validateApiResponse(
        await userOrganizationService.getOrganizationInviteByToken(token),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationInvitesErrors.GET_ORGANIZATION_INVITE_ERROR,
            showSnackbar: false,
          }),
        );
      },
    },
  );
}

export function useAcceptOrganizationInviteMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ token }: { token: string }) => {
      validateApiResponse(await userOrganizationService.acceptOrganizationInvite(token));
      return token;
    },
    {
      onSuccess: (token) => {
        queryClient.removeQueries(getOrganizationInviteQueryKey(token));
        queryClient.invalidateQueries(getOrganizationsQueryKey());
        queryClient.invalidateQueries(getWorkspacesQueryKey());
        queryClient.invalidateQueries(getUserOrganizationInvitesQueryKey());
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationInvitesErrors.SET_ORGANIZATION_INVITE_ROLE_ERROR,
          }),
        );
      },
    },
  );
}

export function useDeclineOrganizationInviteMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ token }: { token: string }) => {
      validateApiResponse(await userOrganizationService.declineOrganizationInvite(token));
      return token;
    },
    {
      onSuccess: (token) => {
        queryClient.removeQueries(getOrganizationInviteQueryKey(token));
        queryClient.invalidateQueries(getUserOrganizationInvitesQueryKey());
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationInvitesErrors.SET_ORGANIZATION_INVITE_ROLE_ERROR,
          }),
        );
      },
    },
  );
}
